import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SpaceBackground from '../components/layout/SpaceBackground';
import { Megaphone, Plus, Trash2, ToggleLeft, ToggleRight, Clock } from 'lucide-react';

const PortalAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAnnouncements = async () => {
    try {
      const response = await api.get(`/api/portal-announcements?include_inactive=${showInactive}`);
      setAnnouncements(response.data);
    } catch (err) {
      console.error('Failed to load announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, [showInactive]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/portal-announcements', {
        title: title.trim(),
        body: body.trim(),
        expires_at: expiresAt || null
      });
      setTitle('');
      setBody('');
      setExpiresAt('');
      setShowForm(false);
      loadAnnouncements();
    } catch (err) {
      alert('Failed to create announcement: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, currentActive) => {
    try {
      await api.patch(`/api/portal-announcements/${id}`, { is_active: !currentActive });
      loadAnnouncements();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement permanently?')) return;
    try {
      await api.delete(`/api/portal-announcements/${id}`);
      loadAnnouncements();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <SpaceBackground>
      <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <Megaphone size={28} /> Portal Announcements
          </h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={{ color: '#94A3B8', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive
            </label>
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              <Plus size={16} /> New Announcement
            </button>
          </div>
        </div>

        {showForm && (
          <form onSubmit={handleCreate} style={{
            background: 'rgba(30,41,59,0.95)', borderRadius: 12, padding: '1.5rem',
            marginBottom: '1.5rem', border: '1px solid rgba(148,163,184,0.2)'
          }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: '#94A3B8', fontSize: 13, display: 'block', marginBottom: 4 }}>Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Announcement title..."
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                  background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 14, boxSizing: 'border-box'
                }}
                required
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ color: '#94A3B8', fontSize: 13, display: 'block', marginBottom: 4 }}>Body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Announcement content..."
                rows={4}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                  background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 14, resize: 'vertical', boxSizing: 'border-box'
                }}
                required
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#94A3B8', fontSize: 13, display: 'block', marginBottom: 4 }}>
                <Clock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Expires at (optional)
              </label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={{
                  padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                  background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 14
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '8px 20px', cursor: 'pointer'
                }}
              >
                {saving ? 'Publishing...' : 'Publish'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                style={{
                  background: 'transparent', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.3)',
                  borderRadius: 8, padding: '8px 20px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p style={{ color: '#94A3B8' }}>Loading...</p>
        ) : announcements.length === 0 ? (
          <p style={{ color: '#94A3B8', textAlign: 'center', marginTop: '3rem' }}>No announcements yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {announcements.map((a) => (
              <div key={a.id} style={{
                background: 'rgba(30,41,59,0.95)', borderRadius: 12, padding: '1.25rem',
                border: `1px solid ${a.is_active ? 'rgba(148,163,184,0.2)' : 'rgba(239,68,68,0.3)'}`,
                opacity: a.is_active ? 1 : 0.6
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: '#E2E8F0', margin: '0 0 6px 0', fontSize: 16 }}>{a.title}</h3>
                    <p style={{ color: '#CBD5E1', margin: '0 0 8px 0', fontSize: 14, whiteSpace: 'pre-wrap' }}>{a.body}</p>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748B' }}>
                      <span>By {a.created_by || 'Unknown'}</span>
                      <span>{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</span>
                      {a.expires_at && <span>Expires: {new Date(a.expires_at).toLocaleDateString()}</span>}
                      <span>{a.read_count} read{a.read_count !== 1 ? 's' : ''}</span>
                      {!a.is_active && <span style={{ color: '#EF4444' }}>Inactive</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                    <button
                      onClick={() => toggleActive(a.id, a.is_active)}
                      title={a.is_active ? 'Deactivate' : 'Activate'}
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: a.is_active ? '#22C55E' : '#EF4444', padding: 4
                      }}
                    >
                      {a.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    </button>
                    <button
                      onClick={() => handleDelete(a.id)}
                      title="Delete"
                      style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#EF4444', padding: 4
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SpaceBackground>
  );
};

export default PortalAnnouncements;
